import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { CustomFieldDefinition } from 'src/app/custom-fields/custom-fields.model';
import { TimezoneService } from 'src/app/shared/services/timezone.service';
import DateUtils from 'src/app/shared/utils/date.utils';
import { CustomFieldConditionEnum } from './custom-field-condition.enum';
import { CustomFieldConditionsUtils } from './custom-field-conditions-utils.class';

@Component({
  selector: 'app-conditions-on-custom-fields',
  templateUrl: './conditions-on-custom-fields.component.html',
  styleUrls: ['./conditions-on-custom-fields.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
  ],
})
export class ConditionsOnCustomFieldsComponent implements OnInit, OnDestroy {
  @Input() data: {
    selectedCustomField: number;
    selectedCondition: string;
    comparedValue: any;
    selectedCustomFieldType;
  };
  @Input() customFields: any[];
  @Input() context: {
    customFieldsMap: Map<number, CustomFieldDefinition>;
    operatorsExcluded?: CustomFieldConditionEnum[];
  };

  @Output() formChanged: EventEmitter<{
    selectedCustomField: number;
    selectedCondition: string;
    comparedValue: any;
    selectedCustomFieldType;
  }> = new EventEmitter();

  form: FormGroup = this.formBuilder.group({
    selectedCustomField: [null, Validators.required],
    selectedCondition: [null, Validators.required],
    comparedValue: [null, Validators.required],
  });

  dateForm = this.formBuilder.group({
    date: [],
    timezone: [this.alcmeonContextService.self.timezone, Validators.required],
    time: this.formBuilder.group({ h: [0], m: [0] }),
  });

  timezoneItems = this.timezoneService.buildTimezoneDropdownItems(this.alcmeonContextService.self.all_timezones);

  conditionsDropdownItems = [];
  customFieldAllowedItems = [];
  booleanItems = [];
  type: string;
  CustomFieldConditionEnum = CustomFieldConditionEnum;
  dateTime: string;
  startHours: any;

  private customFieldConditionsUtils;
  private subscriptions = new Subscription();

  constructor(
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private timezoneService: TimezoneService,
    private alcmeonContextService: AlcmeonContextService
  ) {}

  ngOnInit(): void {
    this.customFieldConditionsUtils = new CustomFieldConditionsUtils(
      this.translateService,
      this.context.operatorsExcluded
    );
    this.booleanItems = this.customFieldConditionsUtils.booleanItems;
    this.form.get('comparedValue').enable({ emitEvent: false });
    this.form.get('selectedCondition').enable({ emitEvent: false });
    this.dateForm.get('date').enable({ emitEvent: false });

    if (this.data) {
      if (this.data.selectedCustomFieldType) {
        this.conditionsDropdownItems = this.customFieldConditionsUtils.conditionsByCustomFieldTypeMap.get(
          this.data.selectedCustomFieldType
        );
      }
      if (this.data.selectedCustomField) {
        const { type, allowed } = this.context.customFieldsMap.get(this.data.selectedCustomField).type;
        this.type = type;
        this.customFieldAllowedItems = allowed;
      }
      this.form.patchValue(this.data);
    }

    this.checkFormControls(this.form.value);

    this.subscriptions.add(
      this.form.valueChanges.subscribe(({ selectedCustomField, selectedCondition, comparedValue }) => {
        this.checkFormControls({ selectedCustomField, selectedCondition });

        if (selectedCustomField || selectedCondition) {
          const customField = this.context.customFieldsMap.get(selectedCustomField);

          this.data = {
            selectedCustomField,
            selectedCondition,
            selectedCustomFieldType: customField.type.type,
            comparedValue,
          };

          this.formChanged.emit(this.data);
        }
      })
    );

    this.subscriptions.add(
      this.form.get('selectedCustomField').valueChanges.subscribe(() => this.setCustomFieldConditions())
    );

    this.subscriptions.add(
      this.form.get('selectedCondition').valueChanges.subscribe(value => {
        if ([CustomFieldConditionEnum.IS_NOT_NULL, CustomFieldConditionEnum.IS_NULL].includes(value)) {
          this.form.get('comparedValue').setValue(null, { emitEvent: false });
        }
      })
    );

    this.subscriptions.add(
      this.dateForm.valueChanges.pipe(debounceTime(300)).subscribe(({ date, timezone, time }) => {
        const momentDate = moment(date);
        const { h = 0, m = 0 } = time;

        const value = DateUtils.createDate({
          year: momentDate.get('year'),
          month: momentDate.get('month'),
          date: momentDate.get('date'),
          hour: h || 0,
          minute: m || 0,
          second: 0,
          timezone,
        });
        this.form.get('comparedValue').setValue(value.toISOString());
      })
    );
  }

  setCustomFieldConditions() {
    const { selectedCustomField } = this.form.getRawValue();
    if (selectedCustomField) {
      this.form.get('comparedValue').setValue(null, { emitEvent: false });
      const { type, allowed } = this.context.customFieldsMap.get(selectedCustomField).type;
      this.type = type;
      this.conditionsDropdownItems = this.customFieldConditionsUtils.conditionsByCustomFieldTypeMap.get(type);
      this.customFieldAllowedItems = allowed;
    }
  }

  updateTime(value: string, pristine = false) {
    const [hours, minutes] = value.split(':');
    this.dateForm.get('time.h').setValue(+hours);
    this.dateForm.get('time.m').setValue(+minutes);
    this.startHours = `${hours}:${minutes}`;

    if (!pristine) {
      this.dateForm.get('time.h').markAsDirty();
      this.dateForm.get('time.m').markAsDirty();
    }
  }

  private checkFormControls({ selectedCustomField, selectedCondition }) {
    if (!selectedCustomField && !selectedCondition) {
      this.form.get('comparedValue').disable({ emitEvent: false });
      this.dateForm.get('date').disable({ emitEvent: false });
    } else {
      this.form.get('comparedValue').enable({ emitEvent: false });
      this.dateForm.get('date').enable({ emitEvent: false });
    }
    if (!selectedCustomField) {
      this.form.get('selectedCondition').disable({ emitEvent: false });
    } else {
      this.form.get('selectedCondition').enable({ emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
